import React, { useState, useEffect } from "react";
import { ChevronLeft, ChevronDown, X } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import {
  useGetAllLeavesAsAdminQuery,
} from "../../slices/leaveApiSlice";
import { useGetAcademyQuery } from "../../slices/userApiSlice";
import CustomSelect from "../../components/CustomSelect/CustomSelect";

export default function AdminCfmLeaveRequests() {
  const navigate = useNavigate();

  // Get user info from Redux store
  const { userInfo } = useSelector((state) => state.auth);

  // RTK Query hooks
  const {
    data: allLeavesResponse,
    isLoading,
    isError,
  } = useGetAllLeavesAsAdminQuery();

  const { data: academiesData, isLoading: academiesLoading } = useGetAcademyQuery();

  const [selectedLeaveType, setSelectedLeaveType] = useState("All");

  // Options for CustomSelect
  const leaveTypeOptions = [
    { value: "All", label: "All" },
    { value: "Casual Leave", label: "Casual Leave" },
    { value: "Earned Leave", label: "Earned Leave" },
    { value: "Manual Leave", label: "Manual Leave" },
  ];
  const [selectedLeave, setSelectedLeave] = useState(null);
  const [showLeaveDetails, setShowLeaveDetails] = useState(false);
  const [localLeaveData, setLocalLeaveData] = useState([]);
  const [academyMap, setAcademyMap] = useState({});


  // Academy map building
  useEffect(() => {
    if (academiesData) {
      let academiesArray = [];

      if (Array.isArray(academiesData)) {
        academiesArray = academiesData;
      } else if (academiesData.academies && Array.isArray(academiesData.academies)) {
        academiesArray = academiesData.academies;
      } else if (academiesData.data && Array.isArray(academiesData.data)) {
        academiesArray = academiesData.data;
      }

      const map = {};
      academiesArray.forEach(academy => {
        const academyId = academy._id || academy.id || academy.academyId;
        const academyName = academy.name || academy.academyName || academy.academy || "Unknown Academy";

        if (academyId) {
          map[academyId] = academyName;
        }
      });

      setAcademyMap(map);
    }
  }, [academiesData]);

  // Transform function for CFM leaves
  const transformLeaveData = (data, academyMapping) => {
    if (!data || !Array.isArray(data)) return [];

    const transformedData = data.map(leave => {
      const days = calculateLeaveDays(leave);
      const isSingleDay = days === 1 || leave.duration === "FULL_DAY" || leave.duration === "HALF_DAY";

      // Extract admin info from workflow notes
      const adminInfo = extractAdminInfo(leave);

      const leaveTypeDisplay = getLeaveTypeDisplay(leave.leaveType);

      // User information
      let userDisplay = "Unknown CFM";
      let userEmail = "";

      userEmail = leave.cfmEmail || leave.Email || leave.userEmail || leave.email || "";
      userDisplay = userEmail || leave.cfmName || leave.user?.name || leave.technicianName || "CFM";

      // Academy information
      const academyId = leave.academyId;
      let academyName = "Unknown Academy";

      if (academyId && academyMapping[academyId]) {
        academyName = academyMapping[academyId];
      } else if (academyId) {
        academyName = `Academy (ID: ${academyId.substring(0, 8)}...)`;
      }

      return {
        id: leave.id || leave._id,
        user: userDisplay,
        userEmail: userEmail,
        position: "CFM",
        type: leaveTypeDisplay,
        reason: leave.reason || "No reason provided",
        days: days,
        startDate: leave.startDate,
        endDate: leave.endDate,
        submittedDate: leave.submittedAt || leave.createdAt,
        status: "Approved",

        // Academy information
        academyId: academyId,
        academyName: academyName,

        // CFM information
        cfmNote: leave.cfmNote || "",
        cfmName: leave.cfmName || userDisplay,
        cfmEmail: leave.cfmEmail || userEmail,

        // Admin information
        adminNote: leave.adminNote || adminInfo.note || "",
        adminName: leave.adminName || adminInfo.name || "",
        adminEmail: leave.managerEmail || adminInfo.email || "",
        adminNoteDate: adminInfo.date || "",

        isHalfDay: leave.duration === "HALF_DAY",
        duration: leave.duration,
        halfDaySession: leave.halfDaySession,
        applicantRole: "CFM",
        originalLeaveType: leave.leaveType,
        isSingleDay: isSingleDay,
        _rawLeave: leave
      };
    });

    // Sort by submitted date (newest first)
    return transformedData.sort((a, b) => new Date(b.submittedDate) - new Date(a.submittedDate));
  };

  // Update local data - filter for APPROVED CFM leaves only
  useEffect(() => {
    if (allLeavesResponse && Object.keys(academyMap).length > 0) {
      const leavesArray = Array.isArray(allLeavesResponse)
        ? allLeavesResponse
        : allLeavesResponse?.leaves || allLeavesResponse?.data || [];

      // Filter for CFM leaves with APPROVED status only
      const approvedCfmLeaves = leavesArray.filter(leave => {
        const applicantRole = detectApplicantRole(leave);
        const isApproved = leave.status === "APPROVED" || leave.status === "Approved";
        return applicantRole === 'CFM' && isApproved;
      });

      const transformedData = transformLeaveData(approvedCfmLeaves, academyMap);
      setLocalLeaveData(transformedData);
    }
  }, [allLeavesResponse, academyMap]);

  // Applicant role detection
  const detectApplicantRole = (leave) => {
    if (leave.applicantRole) {
      return leave.applicantRole;
    }

    if ((leave.cfmId && !leave.managerId && !leave.supervisorId && !leave.technicianId) ||
      (leave.userRole === 'CFM') ||
      (leave.notes && leave.notes.some(note => note.actorRole === 'CFM' && note.action === 'APPLIED'))) {
      return 'CFM';
    }

    return 'OTHER';
  };

  const extractAdminInfo = (leave) => {
    if (!leave.notes || !Array.isArray(leave.notes)) {
      return { note: "", name: "", email: "", date: "" };
    }

    const adminNotes = leave.notes.filter(note =>
      note.actorRole === 'ADMIN' || note.actorRole === 'Admin'
    );

    if (adminNotes.length > 0) {
      const latestNote = adminNotes[adminNotes.length - 1];
      return {
        note: latestNote.message || "",
        name: latestNote.actorName || "Admin",
        email: latestNote.actorEmail || latestNote.actorId || "",
        date: latestNote.timestamp || ""
      };
    }

    return { note: "", name: "", email: "", date: "" };
  };

  const calculateLeaveDays = (leave) => {
    if (!leave.startDate) return 0;

    switch (leave.duration) {
      case "HALF_DAY":
        return 0.5;
      case "FULL_DAY":
        return 1;
      case "MULTI_DAY":
        if (leave.endDate) {
          const start = new Date(leave.startDate);
          const end = new Date(leave.endDate);
          const diffTime = Math.abs(end - start);
          return Math.ceil(diffTime / (1000 * 60 * 60 * 24)) + 1;
        }
        return 1;
      default:
        return 1;
    }
  };

  const getLeaveTypeDisplay = (backendLeaveType) => {
    if (backendLeaveType === 'CASUAL') {
      return 'Casual Leave';
    } else if (backendLeaveType === 'EARN') {
      return 'Earned Leave';
    } else {
      return 'Manual Leave';
    }
  };

  const handleBack = () => {
    navigate("/superadmin/attendance");
  };

  const getStatusColor = (status) => {
    switch (status) {
      case "Approved":
        return "bg-green-100 text-green-800";
      default:
        return "bg-gray-100 text-gray-800";
    }
  };

  const getApplicantTypeColor = (applicantRole) => {
    switch (applicantRole) {
      case "CFM":
        return "bg-purple-100 text-purple-800";
      default:
        return "bg-gray-100 text-gray-800";
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return "";
    try {
      const date = new Date(dateString);
      // Add 6 hours to the date
      date.setHours(date.getHours() + 6);

      return date.toLocaleDateString("en-US", {
        month: "short",
        day: "numeric",
        year: "numeric",
      });
    } catch (error) {
      return dateString;
    }
  };

  const formatDateTime = (dateString) => {
    if (!dateString) return "";
    try {
      const date = new Date(dateString);
      // Add 6 hours to the date
      date.setHours(date.getHours() + 6);

      return date.toLocaleDateString("en-US", {
        month: "short",
        day: "numeric",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit"
      });
    } catch (error) {
      return dateString;
    }
  };

  const handleRowClick = (leave) => {
    setSelectedLeave(leave);
    setShowLeaveDetails(true);
  };

  const handleCloseDetails = () => {
    setShowLeaveDetails(false);
    setSelectedLeave(null);
  };

  // Filter data based on leave type
  const filteredData = localLeaveData.filter(record =>
    selectedLeaveType === "All" || record.type === selectedLeaveType
  );

  // Show loading
  if (isLoading || academiesLoading) {
    return (
      <div className="p-4 sm:p-6 lg:p-8 max-w-10xl mx-auto">
        <div className="flex items-center justify-center h-64">
          <div className="text-lg">Loading CFM leave records...</div>
        </div>
      </div>
    );
  }

  if (isError) {
    return (
      <div className="p-4 sm:p-6 lg:p-8 max-w-10xl mx-auto">
        <div className="flex items-center justify-center h-64">
          <div className="text-lg text-red-600">Error loading CFM leave records. Please try again.</div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-4 sm:p-6 lg:p-8 max-w-10xl mx-auto">
      {/* Header */}
      <div className="flex items-center space-x-1 mb-4 sm:mb-6">
        <button
          aria-label="Back"
          onClick={handleBack}
          className="md:flex w-9 h-9 items-center justify-center rounded-full hover:bg-gray-100 hover:border-gray-400 transition-all"
        >
          <ChevronLeft
            size={24}
            strokeWidth={2}
            className="text-gray-700"
          />
        </button>
        <div className="flex items-center space-x-2 sm:space-x-3">
          <h1 className="text-xl sm:text-2xl font-bold text-gray-800">CFM Leave Records</h1>
        </div>
      </div>

      {/* Records Section */}
      <div className="bg-white rounded-lg border">
        <div className="p-6 border-b">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h2 className="text-xl font-bold text-zinc-800">CFM Approved Leaves</h2>
              <p className="text-sm text-zinc-600 mt-1">
                View approved CFM leave applications
              </p>
            </div>
          </div>

          {/* Filters */}
          <div className="flex flex-col sm:flex-row gap-3 sm:gap-4">
            <div className="flex-1 sm:flex-none">
              <label className="block text-[#ABABAB] text-gray-600 mb-1">Leave Type</label>
              <CustomSelect
                options={leaveTypeOptions}
                value={selectedLeaveType}
                onChange={setSelectedLeaveType}
                placeholder="All"
                minWidth={160}
              />
            </div>
          </div>
        </div>

        {/* Records Table */}
        <div className="bg-white rounded-lg border overflow-hidden">
          <div className="h-[500px] overflow-auto">
            <table className="w-full">
              <thead className="bg-zinc-50 border-b">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-zinc-700 uppercase tracking-wider">
                    CFM
                  </th>
                  <th className="px-4 sm:px-6 py-3 text-left text-[#ABABAB] font-medium text-gray-500 tracking-wider">
                    TYPE
                  </th>
                  <th className="px-4 sm:px-6 py-3 text-left text-[#ABABAB] font-medium text-gray-500 tracking-wider">
                    ACADEMY
                  </th>
                  <th className="px-4 sm:px-6 py-3 text-left text-[#ABABAB] font-medium text-gray-500 tracking-wider">
                    DAYS
                  </th>
                  <th className="px-4 sm:px-6 py-3 text-left text-[#ABABAB] font-medium text-gray-500 tracking-wider">
                    SUBMITTED ON
                  </th>
                  <th className="px-4 sm:px-6 py-3 text-left text-[#ABABAB] font-medium text-gray-500 tracking-wider">
                    FROM
                  </th>
                  <th className="px-4 sm:px-6 py-3 text-left text-[#ABABAB] font-medium text-gray-500 tracking-wider">
                    TO
                  </th>
                  <th className="px-4 sm:px-6 py-3 text-left text-[#ABABAB] font-medium text-gray-500 tracking-wider">
                    STATUS
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-zinc-200">
                {filteredData.length === 0 ? (
                  <tr>
                    <td colSpan="8" className="px-6 py-8 text-center text-zinc-500">
                      No approved CFM leave requests found
                    </td>
                  </tr>
                ) : (
                  filteredData.map((record) => (
                    <tr
                      key={record.id}
                      className="hover:bg-zinc-50 cursor-pointer transition-colors"
                      onClick={() => handleRowClick(record)}
                    >
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-zinc-900">
                        <div>
                          <div>{record.userEmail || record.user}</div>
                          <div className="text-sm text-zinc-600">{record.position}</div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-zinc-900">
                        {record.type}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-zinc-900">
                        {record.academyName}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-zinc-900">
                        {record.days} {record.days === 1 ? "day" : "days"}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-zinc-600">
                        {formatDate(record.submittedDate)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-zinc-600">
                        {formatDate(record.startDate)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-zinc-600">
                        {record.isSingleDay ? formatDate(record.startDate) : formatDate(record.endDate)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span
                          className={`px-3 py-1 rounded-full text-xs font-medium ${getStatusColor(record.status)}`}
                        >
                          {record.status}
                        </span>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* Leave Details Overlay - View Only */}
      {showLeaveDetails && selectedLeave && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-2 sm:p-4 z-50">
          <div className="bg-white rounded-lg w-full max-w-2xl max-h-[90vh] overflow-y-auto mx-2 sm:mx-4">
            <div className="p-3 sm:p-4">
              {/* Header */}
              <div className="flex justify-between items-start mb-3 sm:mb-4">
                <div className="space-y-2 sm:space-y-3">
                  <div className="flex gap-2 flex-wrap">
                    <span className={`inline-flex px-2 sm:px-3 py-1 text-xs sm:text-sm font-semibold rounded-full ${getStatusColor(selectedLeave.status)}`}>
                      {selectedLeave.status}
                    </span>
                    <span className={`inline-flex px-2 sm:px-3 py-1 text-xs sm:text-sm font-semibold rounded-full ${getApplicantTypeColor(selectedLeave.applicantRole)}`}>
                      CFM
                    </span>
                  </div>
                  <div className="flex items-center space-x-2 sm:space-x-3">
                    <div className="w-10 h-10 sm:w-12 sm:h-12 bg-purple-300 rounded-full flex items-center justify-center">
                      <span className="text-white font-semibold text-sm sm:text-base">
                        {selectedLeave.user.charAt(0)}
                      </span>
                    </div>
                    <div>
                      <h3 className="font-semibold text-gray-800 text-sm sm:text-base">
                        {selectedLeave.userEmail || selectedLeave.user}
                      </h3>
                      <p className="text-xs sm:text-sm text-gray-600">{selectedLeave.position}</p>
                    </div>
                  </div>
                </div>

                <button
                  onClick={handleCloseDetails}
                  className="p-1 sm:p-2 hover:bg-gray-100 rounded-lg transition-colors"
                >
                  <X className="w-5 h-5 sm:w-6 sm:h-6 text-gray-600" />
                </button>
              </div>

              {/* Academy Information */}
              <div className="mb-3 sm:mb-4">
                <div className="flex justify-between items-center border-b border-gray-300 pb-2 mb-3 sm:mb-4">
                  <span className="text-[#ABABAB] sm:text-sm font-medium text-gray-700">Academy:</span>
                  <span className="text-gray-900 text-sm sm:text-base font-medium">
                    {selectedLeave.academyName}
                  </span>
                </div>
              </div>

              {/* Leave Type */}
              <div className="mb-1">
                <h3 className="font-semibold text-gray-800 text-base sm:text-lg">{selectedLeave.type}</h3>
              </div>

              <div className="mb-3 sm:mb-4">
                <h4 className="font-medium text-gray-700 mb-1 sm:mb-2 text-sm sm:text-base">Reason:</h4>
                <p className="text-gray-700 bg-gray-50 p-3 sm:p-4 rounded-lg border border-gray-200 text-sm sm:text-base">
                  {selectedLeave.isHalfDay && (
                    <p className="text-sm text-blue-600 mt-1">
                      Half Day - {selectedLeave.halfDaySession === 'MORNING' ? 'Morning' : 'Afternoon'} Session
                    </p>
                  )}
                  {selectedLeave.reason}
                </p>
              </div>

              {/* Dates and Days */}
              <div className="mb-3 sm:mb-4">
                <div className="flex justify-between items-center border-b border-gray-300 pb-2 mb-3 sm:mb-4">
                  <span className="text-[#ABABAB] sm:text-sm font-medium text-gray-700">Submitted On:</span>
                  <span className="text-gray-900 text-sm sm:text-base">
                    {formatDateTime(selectedLeave.submittedDate)}
                  </span>
                </div>

                {selectedLeave.isSingleDay ? (
                  <div className="flex justify-between items-center border-b border-gray-300 pb-2 mb-3 sm:mb-4">
                    <span className="text-[#ABABAB] sm:text-sm font-medium text-gray-700">Leave Date:</span>
                    <span className="text-gray-900 text-sm sm:text-base">{formatDate(selectedLeave.startDate)}</span>
                  </div>
                ) : (
                  <>
                    <div className="flex justify-between items-center border-b border-gray-300 pb-2 mb-3 sm:mb-4">
                      <span className="text-[#ABABAB] sm:text-sm font-medium text-gray-700">From:</span>
                      <span className="text-gray-900 text-sm sm:text-base">{formatDate(selectedLeave.startDate)}</span>
                    </div>
                    <div className="flex justify-between items-center border-b border-gray-300 pb-2 mb-3 sm:mb-4">
                      <span className="text-xs sm:text-sm font-medium text-gray-700">To:</span>
                      <span className="text-gray-900 text-sm sm:text-base">
                        {formatDate(selectedLeave.endDate)}
                      </span>
                    </div>
                  </>
                )}

                <div className="flex justify-between items-center border-b border-gray-300 pb-2">
                  <span className="text-[#ABABAB] sm:text-sm font-medium text-gray-700">Days</span>
                  <span className="text-gray-900 text-sm sm:text-base">
                    {selectedLeave.days} {selectedLeave.days === 1 ? "Day" : "Days"}
                  </span>
                </div>
              </div>

              {/* Notes Section */}
              <div className="bg-gray-50 rounded-lg">
                {selectedLeave.cfmNote && (
                  <div className="p-2 sm:p-3">
                    <div className="flex items-center space-x-2 sm:space-x-3 mb-2 sm:mb-3">
                      <div className="w-8 h-8 sm:w-10 sm:h-10 bg-purple-100 rounded-full flex items-center justify-center">
                        <span className="text-purple-600 font-semibold text-xs sm:text-sm">
                          {selectedLeave.cfmName?.charAt(0) || 'C'}
                        </span>
                      </div>
                      <div>
                        <h4 className="font-semibold text-gray-800 text-sm sm:text-base">
                          {selectedLeave.cfmEmail}
                        </h4>
                        <p className="text-xs sm:text-sm text-gray-600">CFM (Applicant)</p>
                      </div>
                    </div>
                    <div className="p-1">
                      <p className="text-gray-700 text-sm sm:text-base">{selectedLeave.cfmNote}</p>
                    </div>
                  </div>
                )}

                {selectedLeave.adminNote && (
                  <div className="p-2 sm:p-3">
                    <div className="flex items-center space-x-2 sm:space-x-3 mb-2 sm:mb-3">
                      <div className="w-8 h-8 sm:w-10 sm:h-10 bg-blue-100 rounded-full flex items-center justify-center">
                        <span className="text-blue-600 font-semibold text-xs sm:text-sm">
                          {selectedLeave.adminName?.charAt(0) || 'A'}
                        </span>
                      </div>
                      <div>
                        <h4 className="font-semibold text-gray-800 text-sm sm:text-base">
                          {selectedLeave.adminEmail || "Admin"}
                        </h4>
                        <p className="text-xs sm:text-sm text-gray-600">Admin</p>
                      </div>
                    </div>
                    <div className="p-1">
                      <p className="text-gray-700 text-sm sm:text-base">{selectedLeave.adminNote}</p>
                      {selectedLeave.adminNoteDate && (
                        <p className="text-xs text-gray-500 mt-1">
                          Note added on: {formatDateTime(selectedLeave.adminNoteDate)}
                        </p>
                      )}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}